import {
    Typography,
    Table,
    TableRow,
    TableCell,
    TableHead,
    TableBody,
    Checkbox,
    TableContainer,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    Combobox,
    ComboboxOption,
    CircularProgress,
    Box,
    Tooltip,
} from '@bb-ui/react-library';
import {
    ChevronDown,
    ChevronRight,
    LineHorizontal,
} from '@bb-ui/icons/dist/small';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth0Context } from '../../../../../auth/auth0';
import { useStyles } from './IHNodes.styles';
import { useAppConfigContext } from '../../../../../contexts/AppConfigProvider';
import { getTenantId, xconsole } from '../../../../../utilities/utilities';
import { PagingControls } from '../../../../PagingControls/PagingControls';
import {
    getPageNumber,
    getPageNumberOptions,
    updateLocation,
} from '../../../../../utilities/pagination';
import { ILocationState } from '../../../../../types/appUITypes';
import { EditUserError } from '../EditUserError/EditUserError';
import { INodeResponse, IhNodesProps, TreeNode } from './IHNodes.types';
import { ScreenReaderOnly } from '../../../../ScreenReaderOnly/ScreenReaderOnly';

export const IHNodes: React.FunctionComponent<IhNodesProps> = ({
    nodeState = 'configurable',
    disabledTooltip,
    selectAllNodes = false,
    initialNodeIds,
    learnTenantId,
    parentNodes,
    onLoading,
    onError,
    onSelect,
}) => {
    const classes = useStyles();

    const forceAllNodes = nodeState === 'all-ticked-and-disabled';
    const forceNoNodes = nodeState === 'all-unticked-and-disabled';
    const isDisabledByNodeState = nodeState !== 'configurable';

    const { uas, api, pagination } = useAppConfigContext();
    const { user, accessToken } = useAuth0Context();
    const tenantId = getTenantId(user, uas);
    const { t } = useTranslation();

    const location = useLocation<ILocationState>();
    const history = useHistory();

    const ITEMS_PER_PAGE = pagination?.itemsPerPage ?? 10;
    const [pageSize, setPageSize] = useState<number>(ITEMS_PER_PAGE);

    const pageOptions = [
        { value: '10', label: '10' },
        { value: '25', label: '25' },
        { value: '50', label: '50' },
        { value: '100', label: '100' },
    ];

    const [assignedNodes, setAssignedNodes] = useState<string[]>([]);
    useEffect(() => {
        onSelect(learnTenantId, assignedNodes);
    }, [assignedNodes, learnTenantId, onSelect]);

    // States used to track loading and errors
    const [error, setError] = useState<string | undefined>();

    // State managing . The strings are the node ids.
    const [expandedNodes, setExpandedNodes] = useState<string[]>([]); // manages which nodes are expanded
    const [topAncestorIds, setTopAncestorIds] = useState<string[]>([]); // represents which nodes are top most ancestors
    const [loadingNodeIds, setLoadingNodeIds] = useState<string[]>([]); // represents which nodes are loading their children

    useEffect(() => {
        if (forceNoNodes) {
            setAssignedNodes([]);
        } else {
            setAssignedNodes(selectAllNodes ? topAncestorIds : initialNodeIds);
        }
    }, [forceNoNodes, selectAllNodes, topAncestorIds, initialNodeIds]);

    const [nodeOptions, setNodeOptions] = useState<TreeNode[]>([]); // represents the nodes that the user can choose from
    const [noChildrenNodes, setNoChildrenNodes] = useState<string[]>([]); // represents the nodes with no children

    // Initialize nodeOptions and topAncestorIds from parentNodes (passed from EditUserPage)
    useEffect(() => {
        // parentNodes are already TreeNode[] with all required fields
        setNodeOptions(parentNodes);
        setTopAncestorIds(parentNodes.map(n => n.id));
    }, [parentNodes]);

    const tenantUrl = encodeURI(
        `${api!.userManagement}/v2/tenants/${tenantId}/nodes/`,
    );

    // Hooks to manage the loading and error states
    useEffect(() => {
        if (onLoading) {
            onLoading(loadingNodeIds.length > 0);
        }
    }, [loadingNodeIds, onLoading]);

    useEffect(() => {
        if (onError && error) {
            onError(error);
        }
    }, [error, onError]);

    /**
     * Fetches all child nodes of a given parent node (used for expansion only, NOT for root).
     *
     * This function is not using the useApi hook in order to avoid rerendering issues.
     *
     * @param parentNodeId - The ID of the parent node to fetch the child nodes of.
     * @param nextToken - An optional token for fetching the next page of child nodes.
     * @returns A promise that resolves to an array of the child nodes of the parent node.
     *          If an error occurs while fetching the child nodes, the promise resolves to an empty array and an error message is set.
     */
    const fetchAllChildNodes = useCallback(
        async (
            parentNodeId: string,
            nextToken?: string,
        ): Promise<INodeResponse[]> => {
            const fetchUrl = `${tenantUrl}${parentNodeId}`;
            try {
                const response = await axios.get<INodeResponse>(fetchUrl, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    params: {
                        nextToken,
                        learnTenantId,
                    },
                });
                const { data } = response;
                // TODO: add recursive call to get all child nodes, once nextToken is implemented
                return [data];
            } catch {
                setError(`Could not load the children of node ${parentNodeId}`);
                return [];
            }
        },
        [accessToken, tenantUrl, learnTenantId],
    );

    /**
     * Fetches all child nodes of a given parent node and manages the setting of children nodes.
     *
     * If the parent node has no children, its ID is added to `noChildrenNodes`.
     *
     * @param parentNodeId - The ID of the parent node to fetch the child nodes of.
     * @returns A promise that resolves to an array of the child nodes of the parent node.
     *          If the parent node has no children, the promise resolves to an empty array.
     */
    const getChildNodes = useCallback(
        async (parentNodeId: string): Promise<TreeNode[]> => {
            const responses = await fetchAllChildNodes(parentNodeId);
            const nodes = responses
                .map(response => response.nodes[learnTenantId] || [])
                .flat();
            if (nodes.length === 0) {
                setNoChildrenNodes(prevNodes => [...prevNodes, parentNodeId]);
            }
            return nodes;
        },
        [fetchAllChildNodes, learnTenantId],
    );

    /**
     * Finds a node by its ID in a list of nodes.
     *
     * @param nodeId - The ID of the node to find.
     * @param nodes - The list of nodes to search in.
     * @returns The node with the given ID, or undefined if no such node is found.
     */
    const findNodeById = (
        nodeId: string,
        nodes: TreeNode[],
    ): TreeNode | undefined => {
        const node = nodes.find(node => node.id === nodeId);

        if (node) return node;

        return undefined;
    };

    /**
     * Returns all descendant node ids of a node by checking ancestorIds.
     * @param nodes - Array of all TreeNode objects
     * @param nodeId - The id of the node whose descendants you want
     * @returns Array of descendant node ids
     */
    function getAllDescendantIds(nodes: TreeNode[], nodeId: string): string[] {
        return nodes
            .filter(
                node => node.ancestorIds && node.ancestorIds.includes(nodeId),
            )
            .map(node => node.id);
    }

    /**
     * Updates the selected nodes.
     *
     * If the given node ID is already in the selected nodes, it is removed.
     * Otherwise, it is added to the selected nodes.
     * In either case, all child nodes of the given node are removed from the selected nodes.
     *
     * @param nodeId - The ID of the node to add or remove.
     * @param nodes - The list of all nodes.
     */
    const updateSelected = (nodeId: string, nodes: TreeNode[]) => {
        const node = findNodeById(nodeId, nodes);

        // Prevent selection of nodes that are too deep
        if (node && node.level >= 4) {
            xconsole.warn(
                `Cannot select node at level ${node.level}. Maximum allowed level is 4.`,
            );
            return;
        }

        // If the node ID is already in unsavedNodeAssignments, it is removed.
        // If the node ID is not in unsavedNodeAssignments, it is added.
        const updatedSelected = assignedNodes.includes(nodeId)
            ? assignedNodes.filter(g => g !== nodeId)
            : [...assignedNodes, nodeId];

        // remove all the children of the newly selected node and dedupe
        const descendantsIds = getAllDescendantIds(nodes, nodeId);
        const updatedSelectedFiltered = [
            ...new Set(
                updatedSelected.filter(g => !descendantsIds.includes(g)),
            ),
        ];

        setAssignedNodes(updatedSelectedFiltered);
    };

    /**
     * Checks if all top ancestor nodes are selected.
     *
     * @returns True if all top ancestor nodes are selected, false otherwise.
     *          If there are no top ancestor nodes, false is returned.
     */
    const allTopAncesorsAreSelected = (): boolean => {
        if (topAncestorIds.length === 0) return false;
        return topAncestorIds.every(ancestorId =>
            assignedNodes.includes(ancestorId),
        );
    };

    /**
     * Selects/deselects all nodes. Selects all nodes if not all top ancestor
     * nodes are selected, deselects all nodes otherwise.
     *
     */

    const selectAllNodesChecked = () => {
        const selectedIds = allTopAncesorsAreSelected() ? [] : topAncestorIds;
        setAssignedNodes(selectedIds);
    };

    /**
     * Handles the change of the page size.
     *
     * Updates the page size and resets the current page to 1.
     *
     * @param newPageSize - The new page size.
     */
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        updateLocation({ page: 1 }, history, location);
    };

    /**
     * Gets the style for a row based on its level.
     *
     * The left padding of the row is increased by 2rem for each level.
     *
     * @param level - The level of the row.
     * @returns A style object with the left padding set based on the level.
     *          If the level is 0 or less, an empty object is returned.
     */
    const getRowStyle = (level: number) => {
        if (level <= 0) return {};
        const padding = `${1 + level * 2}rem`;
        return {
            paddingLeft: padding,
        };
    };

    /**
     * Handles the click event on a node to expand it.
     *
     * When a node is clicked, its child nodes are fetched and added to the node options.
     * The clicked node is also added to the expanded nodes and the loading nodes.
     * Once the child nodes are fetched, the clicked node is removed from the loading nodes.
     *
     * @param parentNode - The node that was clicked.
     * @param childNodeLevel - The level of the child nodes of the clicked node.
     */
    const handleNodeExpandClick = (
        parentNode: TreeNode,
        childNodeLevel: number,
    ) => {
        setLoadingNodeIds(prevLoadingNodeIds => [
            ...prevLoadingNodeIds,
            parentNode.id,
        ]);
        setExpandedNodes(prevExpandedNodes => [
            ...prevExpandedNodes,
            parentNode.id,
        ]);
        getChildNodes(parentNode.id).then(nodes => {
            const updatedNodes: TreeNode[] = nodes.map(node => ({
                ...node,
                ancestorIds: [...(parentNode.ancestorIds ?? []), parentNode.id],
                level: childNodeLevel,
                sortableName: `${parentNode.sortableName}|${node.name}`,
            }));

            const childNodeIds = updatedNodes.map(n => n.id);

            // Merge children into nodeOptions safely and also update the parent
            // node's childrenIds. Use functional updates to avoid stomping on
            // concurrent changes.
            setNodeOptions(prevNodeOptions => {
                // Add or replace the parent node with updated childrenIds
                const replaced = prevNodeOptions.map(n =>
                    n.id === parentNode.id
                        ? { ...n, childrenIds: childNodeIds }
                        : n,
                );

                // Append any new child nodes that aren't already present
                const existingIds = new Set(replaced.map(n => n.id));
                const toAppend = updatedNodes.filter(
                    n => !existingIds.has(n.id),
                );

                return [...replaced, ...toAppend];
            });

            setLoadingNodeIds(prevLoadingNodeIds =>
                prevLoadingNodeIds.filter(id => id !== parentNode.id),
            );
        });
    };

    /**
     * Handles the click event on a node to collapse it.
     *
     * When a node is clicked, it is removed from the expanded nodes and its descendants are removed from the node options.
     *
     * @param parentNode - The node that was clicked.
     */
    const handleNodeCollapseClick = (parentNode: TreeNode) => {
        const nodesToCollapse = nodeOptions
            .filter(node => node.ancestorIds?.includes(parentNode.id))
            .map(node => node.id);

        setExpandedNodes(prevExpandedNodes =>
            prevExpandedNodes.filter(
                nodeId =>
                    nodeId !== parentNode.id &&
                    !nodesToCollapse.includes(nodeId),
            ),
        );

        setNodeOptions(prevNodeOptions =>
            prevNodeOptions.filter(
                node => !node.ancestorIds?.includes(parentNode.id),
            ),
        );
    };

    const handleClosedChevronKeyDown = (
        event: any,
        parentNode: TreeNode,
        childNodeLevel: number,
    ) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleNodeExpandClick(parentNode, childNodeLevel);
        }
    };

    const handleOpenedChevronKeyDown = (event: any, parentNode: TreeNode) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleNodeCollapseClick(parentNode);
        }
    };

    /**
     * Gets the arrow icon for a parent node based on its state.
     *
     * If the parent node is expanded, a ChevronDown icon is returned. Indicates that the children are visible.
     * If the parent node is not expanded, a ChevronRight icon is returned. Indicates that the children are hidden.
     * If the parent node has no children, a LineHorizontal icon is returned. Indicates that the node has no children.
     * If the parent node's level is less than 0, null is returned.
     *
     * @param parentNode - The parent node to get the arrow icon for.
     * @returns A ChevronDown, ChevronRight, or LineHorizontal icon, or null, based on the state of the parent node.
     */
    const getArrow = (parentNode: TreeNode) => {
        if (parentNode.level < 0) return null;

        const expanded = expandedNodes.includes(parentNode.id);
        const childNodeLevel = parentNode.level + 1;
        const hasNoChildren = noChildrenNodes.includes(parentNode.id);

        let expanderProps: React.DetailedHTMLProps<
            React.ButtonHTMLAttributes<HTMLButtonElement>,
            HTMLButtonElement
        > & { 'data-testid'?: string };

        let innerElement: React.ReactNode;

        if (hasNoChildren) {
            expanderProps = {
                'data-testid': `no-children-icon-${parentNode.id}`,
                tabIndex: -1,
                disabled: true,
            };
            innerElement = (
                <LineHorizontal className={classes.noChildrenFoundIcon} />
            );
        } else if (!expanded) {
            expanderProps = {
                key: `node-${parentNode.id}-expansion-icon`,
                'data-testid': `expand-children-icon-${parentNode.id}`,
                onClick: () =>
                    handleNodeExpandClick(parentNode, childNodeLevel),
                tabIndex: 0,
                onKeyDown: (event: any) =>
                    handleClosedChevronKeyDown(
                        event,
                        parentNode,
                        childNodeLevel,
                    ),
                'aria-expanded': 'false',
                'aria-label': t(
                    'settings.editUser.nodes.voiceOverTooltips.expandNodes',
                    { nodeName: parentNode.name },
                ),
            };
            innerElement = (
                <ChevronRight className={classes.expandChildrenIcon} />
            );
        } else {
            expanderProps = {
                key: `node-${parentNode.id}-expansion-icon`,
                'data-testid': `collapse-children-icon-${parentNode.id}`,
                onClick: () => handleNodeCollapseClick(parentNode),
                tabIndex: 0,
                onKeyDown: (event: any) =>
                    handleOpenedChevronKeyDown(event, parentNode),
                'aria-expanded': 'true',
                'aria-label': t(
                    'settings.editUser.nodes.voiceOverTooltips.collapseNodes',
                    { nodeName: parentNode.name },
                ),
            };
            innerElement = (
                <ChevronDown className={classes.collapseChildrenIcon} />
            );
        }

        return (
            <>
                <button className={classes.expansionWrapper} {...expanderProps}>
                    {innerElement}
                </button>
                {hasNoChildren && (
                    <ScreenReaderOnly type="span" aria-live="polite">
                        {t(
                            'settings.editUser.nodes.voiceOverTooltips.noChildNodesExpansionAnnounce',
                            { nodeName: parentNode.name },
                        )}
                    </ScreenReaderOnly>
                )}
            </>
        );
    };

    /**
     * Updates the current page.
     *
     * @param newPage - The new page number.
     */
    const updateCurrentPage = (newPage: number) => {
        updateLocation({ page: newPage }, history, location);
    };

    /**
     * Checks if any ancestor nodes of a given node is selected.
     *
     * @param node - The node to check the ancestor nodes of.
     * @returns True if any ancestor node of the node is selected, false otherwise.
     *          If the node has no ancestor nodes, false is returned.
     */
    const isAncestorSelected = (node: TreeNode): boolean => {
        if (node.ancestorIds && node.ancestorIds.length > 0) {
            return node.ancestorIds.some(ancestorId =>
                assignedNodes.includes(ancestorId),
            );
        }
        return false;
    };

    /**
     * Sorts a list of nodes by their sortable names.
     *
     * @param nodes - The list of nodes to sort.
     * @returns A new array of the nodes sorted by their sortable names.
     *          If the input is undefined, an empty array is returned.
     */
    const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
        if (nodes === undefined) {
            return [];
        }
        return nodes.sort((a, b) =>
            a.sortableName.localeCompare(b.sortableName),
        );
    };

    /**
     * An object that provides the text values for the ComboBox component.
     *
     * The text values are localized using the `t` function.
     */
    const comboBoxTextValues = {
        announceOptionSelected: (option: any) =>
            t('settings.editUser.comboBox.optionSelected', {
                option: option.label,
            }),
        announceOptionDeselected: (option: any) =>
            t('settings.editUser.comboBox.optionDeselected', {
                option: option.label,
            }),
        announceValueCleared: t('settings.editUser.comboBox.valueCleared'),
        announceSearchResults: (count: any) => {
            switch (count) {
                case 0:
                    return t('settings.editUser.comboBox.noResultsFound');
                default:
                    return t('settings.editUser.comboBox.resultsFound', {
                        count,
                    });
            }
        },
        noResults: () => t('settings.editUser.comboBox.noResultsFound'),
        clearButtonLabel: t('settings.editUser.comboBox.clearButton'),
        searchLabel: t('settings.editUser.comboBox.searchLabel'),
    };

    /**
     * Renders a loading indicator for a node.
     *
     * If the node is currently loading, a CircularProgress component is returned.
     * Otherwise, null is returned.
     *
     * @param id - The ID of the node.
     * @returns A CircularProgress component if the node is currently loading, or null otherwise.
     */
    const renderLoader = (id: string) => {
        if (loadingNodeIds.includes(id)) {
            return (
                <CircularProgress
                    id="icp1"
                    data-testid={`loading-children-icon-${id}`}
                    label=""
                    size="12"
                    className={classes.loadingChildrenIcon}
                />
            );
        }
        return null;
    };

    const getSetSize = (node: TreeNode, nodeOptions: TreeNode[]): number => {
        const sameLevelNodes = nodeOptions.filter(n => n.level === node.level);
        return sameLevelNodes.length;
    };

    const getPositionInSet = (
        node: TreeNode,
        nodeOptions: TreeNode[],
    ): number => {
        const sameLevelNodes = nodeOptions
            .filter(
                n =>
                    n.level === node.level &&
                    JSON.stringify(n.ancestorIds?.sort()) ===
                        JSON.stringify(node.ancestorIds?.sort()),
            )
            .sort((a, b) => a.sortableName.localeCompare(b.sortableName));
        return sameLevelNodes.findIndex(n => n.id === node.id) + 1;
    };

    const handleCheckboxKeyDown = (
        event: any,
        nodeId: string,
        isTooDeep = false,
    ) => {
        if (event.key === 'Enter' && !isTooDeep) {
            event.preventDefault();
            updateSelected(nodeId, nodeOptions);
        }
    };

    /**
     * Renders a table row for each node.
     *
     * The nodes are sorted and paginated, and a table row is rendered for each node.
     * Each table row includes a checkbox and the name of the node.
     *
     * @returns An array of table row elements.
     */
    const renderTableRow = () =>
        sortNodes(nodeOptions)
            .slice(
                (getPageNumber(location) - 1) * pageSize,
                getPageNumber(location) * pageSize,
            )

            .map(node => {
                const isAncestorNodeSelected = isAncestorSelected(node);
                const isTooDeep = node.level >= 4; // Disable nodes at level 4 and below
                const isDisabled =
                    isDisabledByNodeState ||
                    isAncestorNodeSelected ||
                    isTooDeep;

                const renderCheckbox = () => (
                    <span>
                        <Checkbox
                            data-testid={`node-checkbox-${node.id}`}
                            id={`node-checkbox-${node.id}-${node.level}`}
                            className={`${
                                isDisabled ? classes.disabledCheckBox : ''
                            }`}
                            disabled={isDisabled}
                            checked={
                                !forceNoNodes &&
                                (forceAllNodes ||
                                    assignedNodes.includes(node.id) ||
                                    isAncestorNodeSelected)
                            }
                            onChange={() => {
                                if (!isTooDeep) {
                                    // Only allow changes if not too deep
                                    updateSelected(node.id, nodeOptions);
                                }
                            }}
                            onKeyDown={event =>
                                handleCheckboxKeyDown(event, node.id, isTooDeep)
                            }
                            inputProps={{ 'aria-label': `Select ${node.name}` }}
                            title={
                                isTooDeep
                                    ? t(
                                          'settings.editUser.nodes.tooDeepTooltip',
                                      )
                                    : undefined
                            }
                        />
                    </span>
                );

                return (
                    <TableRow
                        key={node.id}
                        role="row"
                        aria-level={node.level + 1}
                        aria-posinset={getPositionInSet(node, nodeOptions)}
                        aria-setsize={getSetSize(node, nodeOptions)}
                        aria-expanded={expandedNodes.includes(node.id)}
                        aria-selected={
                            assignedNodes.includes(node.id) ||
                            isAncestorNodeSelected
                        }
                        aria-disabled={isDisabled}
                    >
                        <TableCell>
                            {isDisabledByNodeState ? (
                                <Tooltip title={disabledTooltip ?? ''}>
                                    {renderCheckbox()}
                                </Tooltip>
                            ) : (
                                renderCheckbox()
                            )}
                        </TableCell>
                        <TableCell
                            style={{
                                ...getRowStyle(node.level),
                                ...(isTooDeep ? { opacity: 0.6 } : {}), // Visual indicator for disabled nodes
                            }}
                            role="gridcell"
                        >
                            <div className={classes.nodeRow}>
                                {getArrow(node)}
                                <p
                                    tabIndex={0}
                                    id={`node-name-${node.id}-${node.level}`}
                                    className={
                                        isTooDeep
                                            ? classes.disabledNodeName
                                            : ''
                                    }
                                >
                                    {node.name}
                                    {isTooDeep && (
                                        <span
                                            className={classes.levelIndicator}
                                        >
                                            {t(
                                                'settings.editUser.nodes.levelTooDeep',
                                            )}
                                        </span>
                                    )}
                                </p>
                                {renderLoader(node.id)}
                            </div>
                        </TableCell>
                    </TableRow>
                );
            });

    /**
     * Renders an error message.
     *
     * @returns An `EditUserError` component with the current error message.
     */
    const renderError = () => <EditUserError error={error ?? ''} />;

    function renderAllNodesCheckbox() {
        return (
            <span>
                <Checkbox
                    data-testid="select-all-checkbox"
                    disabled={isDisabledByNodeState}
                    checked={
                        !forceNoNodes &&
                        (forceAllNodes || allTopAncesorsAreSelected())
                    }
                    onChange={selectAllNodesChecked}
                    id="select-all-nodes-checkbox"
                    inputProps={{ 'aria-label': 'Select all nodes' }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            selectAllNodesChecked();
                        }
                    }}
                />
            </span>
        );
    }

    const renderNodeTable = () => (
        <>
            <TableContainer>
                <Table
                    role="treegrid"
                    aria-multiselectable="true"
                    aria-label={t('settings.editUser.nodes.title')}
                    aria-rowcount={nodeOptions.length}
                    aria-colcount={2}
                >
                    <TableHead>
                        <TableRow role="row">
                            <TableCell role="gridcell" padding="checkbox">
                                {isDisabledByNodeState ? (
                                    <Tooltip title={disabledTooltip ?? ''}>
                                        {renderAllNodesCheckbox()}
                                    </Tooltip>
                                ) : (
                                    renderAllNodesCheckbox()
                                )}
                            </TableCell>
                            <TableCell role="columnheader">
                                {t('settings.editUser.nodes.nodeColumnName')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{renderTableRow()}</TableBody>
                </Table>
            </TableContainer>
            <div className={classes.pagination}>
                <Combobox
                    id="page-size-combobox"
                    floatingLabel
                    className={classes.pageSizeComboBox}
                    isSearchable={false}
                    label="Items"
                    defaultValue={{
                        value: pageSize.toString(),
                        label: pageSize.toString(),
                    }}
                    onChange={(option: ComboboxOption) => {
                        handlePageSizeChange(parseInt(option.value, 10));
                    }}
                    strings={comboBoxTextValues}
                    options={pageOptions}
                />
                <PagingControls
                    pageNumber={getPageNumber(location)}
                    pageNumberOptions={getPageNumberOptions(
                        pageSize,
                        nodeOptions.length,
                    )} // Get this from the size of the nodeOptions array
                    updateCurrentPage={updateCurrentPage}
                />
            </div>
        </>
    );

    const renderNoNodesMessage = () => (
        <Typography className={classes.noContentMessage}>
            {t('settings.editUser.nodes.institutionHasNoHierarchy')}
        </Typography>
    );

    const renderContent = () => {
        if (error) {
            return renderError();
        }
        if (nodeOptions.length > 0) {
            return renderNodeTable();
        }
        return renderNoNodesMessage();
    };

    return (
        <div
            data-testid="ih-nodes-container"
            className={classes.ihNodeContainer}
        >
            <ExpansionPanel
                defaultExpanded={true}
                className={classes.tableContainer}
            >
                <ExpansionPanelSummary
                    id="nodes-assigned-summary"
                    aria-controls="nodes-assigned-details"
                >
                    <div className={classes.ihNodesSummary}>
                        <Typography
                            variant="h2"
                            className={classes.infoTextMargin}
                        >
                            {t('settings.editUser.nodes.title')}
                        </Typography>
                        <Typography className={classes.infoTextMargin}>
                            {t('settings.editUser.nodes.description')}
                        </Typography>
                    </div>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <Box
                        data-testid="user-nodes-combo-box-container"
                        className={classes.tableContainer}
                    >
                        {renderContent()}
                    </Box>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        </div>
    );
};
